import React, { useContext, useRef } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import { useLocation } from 'react-router-dom';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import changeLabelOfContent from './ChangeLabelOfContent';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useParams } from 'react-router-dom';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import Sentiment from 'components/Dashboard/components/Sentiment';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import time from 'utils/time';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemHighlightSection,
  ListItemLink,
} from 'components/Dashboard/ListControl/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
const ParliamentaryItem = ({ listRow }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults } = useContentResults();
  const location = useLocation();

  const { search, referenceState } = state;
  const { activeReference } = referenceState;
  const { list, activeSearch } = search;
  const { ParliamentaryRecord } = contentResults;
  const { currentId } = ParliamentaryRecord ?? {};

  const {
    listContext = {},
    feedOverrides = {},
    display = {},
    interactions = {},
    external = {},
    sectionMeta = {},
  } = listRow ?? {};
  const { index, item, scrollBarRef, array, zIndex, listView } = listContext;
  const { explanations, feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const {
    notShowShare,
    showHighlights,
    hideYellowDot,
    showContentTypeBadge,
    tagsFixedPosition,
    onlyEmbargoedTag,
    tooltipText,
  } = display;
  const { itemOnclick, thumbDownProps, itemActionsProps, showPriorityBadge, setActiveUpdate } = interactions;
  const { embedShowTags, itemFromWidget } = external;
  const { numberOfContributions, collapsedHits } = sectionMeta;

  const feedItemFlags = propsFeedItemFlags ?? ParliamentaryRecord?.feedItemFlags ?? [];
  const params = useParams();
  const { inboxSection } = useMainSectionParameter(params);
  const { agencyUser } = useTopicOrClientWord();
  const itemClass = () => {
    switch (item.house) {
      case 'commons':
        return 'legislation-commons';
      case 'lords':
        return 'legislation-lords';
      default:
        return 'legislation-grey';
    }
  };

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useListItemController({
    item,
    index,
    feedItemFlags: propsFeedItemFlags,
    hideYellowDot,
    listView,
    thumbDownProps,
    showPriorityBadge,
    itemName: 'ParliamentaryRecord',
  });

  const { rememberPosition } = useRememberPosition();

  const urlParameterCondition =
    getUrlParam('search') ||
    getUrlParam('or') ||
    (getUrlParam('topic-id') && getUrlParam('topic-id') !== 'none') ||
    location.pathname.includes('/stakeholders') ||
    location.pathname.startsWith('/embed') ||
    location.pathname.startsWith('/search-results') ||
    showHighlights;

  const highlightConditional = urlParameterCondition || item.contentType !== 'ParliamentaryContribution';
  const stakeholderPage = location.pathname.includes('/stakeholders');
  const searchStakeholderCondition =
    !stakeholderPage || (stakeholderPage && (getUrlParam('search') || getUrlParam('topic-id')));

  const inboxMessage = () => {
    let numberOfTopics = feedItemFlags?.[index]?.matchingKeywordLists?.length;
    return inboxSection && list?.id === null
      ? `${agencyUser ? 'for' : 'from'} ${numberOfTopics} ${agencyUser ? 'client' : 'topic'}${
          numberOfTopics === 1 ? '' : 's'
        }`
      : '';
  };

  const createItemNumber = ({ sentiment, hits }) => {
    if (sentiment && hits) {
      let items = hits.filter((item) => item?.hit?.sentiment === sentiment);
      let number = items?.length;
      if (number > 0) {
        return `<p class="mt-2 mb-0 paragraph-p3"><span class="d-inline-block mr-2 sentiment-dot sentiment-dot-${sentiment.toLowerCase()}"></span>${number} ${sentiment.toLowerCase()} contribution${
          number === 1 ? '' : 's'
        }</p>`;
      }
      return '';
    }
    return '';
  };

  const compileCollapseHits = ({ hits }) => {
    return `${createItemNumber({ sentiment: 'Positive', hits })}${createItemNumber({
      sentiment: 'Negative',
      hits,
    })}${createItemNumber({ sentiment: 'Mixed', hits })}${createItemNumber({ sentiment: 'Neutral', hits })}`;
  };

  const contributionContentTypes = [
    'ScotlandCountrySpecificParliamentaryContribution',
    'ParliamentaryContribution',
    'WalesCountrySpecificParliamentaryContribution',
    'SeneddCommitteeTranscriptContribution',
    'CREC',
  ];

  const crecType = item?.contentType === 'CREC';

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });

  return (
    <>
      <ListItemLink
        to={locationDescriptor(
          `${item.contentUrl}`,
          urlParameterCondition && !!item.contributionId ? `#contribution-${item.contributionId}` : ''
        )}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        onClick={(e) => {
          if (rateUpdatesMode && setActiveUpdate) {
            preventLink(e);
            setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
            return;
          }
          rememberPosition('ParliamentaryRecord', scrollBarRef, index);
          if (itemOnclick) {
            itemOnclick(e);
          }
        }}
        tooltipText={tooltipText}
      >
        <ListItemContainer
          className={`my-2 ${currentId === item.id ? 'active' : ''} ${
            unseenConditional ? 'unseen-item' : ''
          } adjustment-unseen-dot-parliamentary`}
          as='li'
        >
          <div className='content-item'>
            {unseenConditional && <span className='unseen-dot' />}
            <div className='py-3 px-4 parliamentary-item-padding'>
              <div className='d-flex align-items-baseline'>
                <div className='flex-grow-1 pr-1 pr-sm-0'>
                  <div className='d-flex align-items-baseline'>
                    {showContentTypeBadge && (
                      <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                        <ContentTypeBadge objectType={item.type} />
                      </div>
                    )}
                    <h4 className='mb-2 content-item-title mb-1'>{item.title}</h4>
                  </div>
                  <div className={`w-100`}>
                    <p className={`heading-date mb-0 ${highlightConditional ? '' : 'information-date'}`}>
                      {crecType ? (
                        <>
                          <span className={`${itemClass()}`}>Congressional Record</span>
                          <span className='dot'></span>
                          {item.house && (
                            <>
                              <span className={`${itemClass()}`}>{item.house}</span>
                              <span className='dot'></span>
                            </>
                          )}
                          {item.congressionalPagePrefix && item.congressionalPageNumber && (
                            <>
                              <span className={`${itemClass()}`}>
                                {item.congressionalPagePrefix}
                                {item.congressionalPageNumber}
                                {item.congressionalOrderOnPage > 0 ? `-${item.congressionalOrderOnPage}` : ''}
                              </span>
                              <span className='dot'></span>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {(item.house || item.contentType) && (
                            <>
                              <span className={`${itemClass()}`}>{`${
                                item.house && item.house !== 'n/a'
                                  ? item.house.charAt(0).toUpperCase()
                                  : changeLabelOfContent(item.contentType)
                              }${item.house && item.house !== 'n/a' ? item.house.slice(1) : ''}`}</span>
                              <span className='dot'></span>
                            </>
                          )}
                          {item.hansardContentType !== '' && !!item.hansardContentType
                            ? item.hansardContentType
                            : item.contentType && item.contentType === 'QuestionAndAnswer'
                              ? 'Written Q&As (Westminster)'
                              : ''}
                          {((item.hansardContentType !== '' && !!item.hansardContentType) ||
                            item.contentType === 'QuestionAndAnswer') && <span className='dot'></span>}
                          {(item.contentType && item.contentType.includes('QuestionAndAnswer')) ||
                          (item.type && item.type.includes('WrittenQuestion'))
                            ? item.questionHasAnswer === 'True'
                              ? `Answered on `
                              : `Tabled on `
                            : ''}
                          {(item?.contentType === 'Edm' || item.type === 'Edm') && (
                            <>
                              Early Day Motion (Westminster) <span className='dot' />
                              {'Tabled on '}
                            </>
                          )}
                        </>
                      )}
                      <span className='date'>
                        {time(item.dateTime, { utc: true, skipTimezone: true }).formatLongDate()}
                      </span>
                    </p>
                  </div>
                </div>
                {!notShowShare && (
                  <ItemControllerActions
                    rateUpdatesMode={rateUpdatesMode}
                    ratingFeedback={feedItemFlags?.[index]?.importanceRating}
                    priorityBadge={
                      priorityBadgeCondition ? (
                        <PriorityBadge
                          item={item}
                          feedItemFlags={feedItemFlags[index]}
                          numberOfContributions={numberOfContributions}
                        />
                      ) : null
                    }
                    actions={
                      <>
                        <Sentiment item={item} />
                        <GeneralItemActions
                          item={item}
                          index={index}
                          thumbDownProps={thumbDownProps}
                          itemActionsProps={itemActionsProps}
                        />
                      </>
                    }
                  />
                )}
              </div>

              <ListItemHighlightSection
                show={highlightConditional}
                bodyRef={titleBodyComponent}
                contributorName={item.contributorName}
                bodyMarkup={createMarkup(bodySnippet)}
                tags={
                  ((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
                    <TagsLists
                      feedItemFlags={feedItemFlags}
                      explanations={explanations ?? ParliamentaryRecord?.explanations}
                      index={index}
                      title={'Parliamentary record'}
                      section={'ParliamentaryRecord'}
                      tagsFixedPosition={tagsFixedPosition}
                      scrollBarRef={scrollBarRef}
                      onlyEmbargoedTag={onlyEmbargoedTag}
                    />
                  )
                }
              />
              {!!numberOfContributions &&
                contributionContentTypes?.indexOf(item?.contentType) >= 0 &&
                (activeReference || activeSearch || location.pathname.includes('/stakeholders/')) && (
                  <div className='pt-2'>
                    <p
                      className='mb-0 text-light-blue paragraph-p3 d-inline-block'
                      data-tooltip-html={
                        collapsedHits && compileCollapseHits({ hits: collapsedHits }) !== ''
                          ? `<div class="pt-1 px-3 pb-3">${compileCollapseHits({ hits: collapsedHits })}</div>`
                          : ''
                      }
                      data-tooltip-id={'clickeable-tooltip'}
                      data-tooltip-class-name={'contributions-tooltip sentiment-dot-tooltip'}
                    >
                      {numberOfContributions} {searchStakeholderCondition ? 'relevant' : ''} {'contribution'}
                      {numberOfContributions === 1 ? '' : 's'} {inboxMessage()}
                    </p>
                  </div>
                )}
            </div>
          </div>
        </ListItemContainer>
      </ListItemLink>
    </>
  );
};

export default ParliamentaryItem;
